import { Link } from "react-router-dom";
import { LinkedinLogo, GithubLogo, XLogo, MapPin, Phone, EnvelopeSimple, ArrowUpRight } from "@phosphor-icons/react";
import { COMPANY, SERVICES, PRODUCTS } from "@/data/content";
import Logo from "@/components/Logo";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-50 border-t border-slate-200 mt-12"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Logo size={42} withWordmark />
          <p className="mt-6 text-sm text-slate-600 max-w-md leading-relaxed">
            {COMPANY.pitch}
          </p>

          <div className="mt-8 flex items-center gap-3">
            <a
              href={COMPANY.social.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-linkedin-link"
              aria-label="LinkedIn"
              className="w-10 h-10 grid place-items-center border border-slate-300 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors text-slate-700"
            >
              <LinkedinLogo size={18} weight="bold" />
            </a>
            <a
              href={COMPANY.social.twitter}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-twitter-link"
              aria-label="X / Twitter"
              className="w-10 h-10 grid place-items-center border border-slate-300 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors text-slate-700"
            >
              <XLogo size={18} weight="bold" />
            </a>
            <a
              href={COMPANY.social.github}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-github-link"
              aria-label="GitHub"
              className="w-10 h-10 grid place-items-center border border-slate-300 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors text-slate-700"
            >
              <GithubLogo size={18} weight="bold" />
            </a>
          </div>

          <a
            href={COMPANY.social.newsletter}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-newsletter-link"
            className="mt-8 inline-flex items-center gap-3 px-4 py-3 border border-slate-300 hover:border-[#1D4ED8] hover:bg-[#DBEAFE]/60 transition-colors group"
          >
            <LinkedinLogo size={18} weight="fill" className="text-[#1D4ED8]" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-900">
              Subscribe to our LinkedIn Newsletter
            </span>
            <ArrowUpRight size={14} weight="bold" className="text-slate-500 group-hover:text-[#1D4ED8]" />
          </a>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Services</div>
          <ul className="space-y-2">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-slate-600 hover:text-[#1D4ED8] transition-colors"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Products</div>
          <ul className="space-y-2">
            {PRODUCTS.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`footer-product-${p.slug}`}
                  className="text-sm text-slate-600 hover:text-[#1D4ED8] transition-colors"
                >
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>

          <div className="overline mt-8 mb-4">Company</div>
          <ul className="space-y-2">
            <li>
              <Link to="/about" className="text-sm text-slate-600 hover:text-[#1D4ED8] transition-colors" data-testid="footer-about-link">About</Link>
            </li>
            <li>
              <Link to="/use-cases" className="text-sm text-slate-600 hover:text-[#1D4ED8] transition-colors" data-testid="footer-use-cases-link">Use Cases</Link>
            </li>
            <li>
              <Link to="/contact" className="text-sm text-slate-600 hover:text-[#1D4ED8] transition-colors" data-testid="footer-contact-link">Contact</Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Reach Us</div>
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="flex gap-3">
              <MapPin size={18} className="text-[#1D4ED8] mt-0.5 shrink-0" weight="bold" />
              <span>
                {COMPANY.address}<br />{COMPANY.city}
              </span>
            </li>
            <li className="flex gap-3">
              <Phone size={18} className="text-[#1D4ED8] shrink-0 mt-0.5" weight="bold" />
              <div className="flex flex-col">
                {COMPANY.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/[^+0-9]/g, '')}`} className="hover:text-[#1D4ED8]">{p}</a>
                ))}
              </div>
            </li>
            <li className="flex gap-3">
              <EnvelopeSimple size={18} className="text-[#1D4ED8] shrink-0" weight="bold" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-[#1D4ED8]">{COMPANY.email}</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs font-mono text-slate-500 uppercase tracking-[0.18em]">
          <div>© {new Date().getFullYear()} Theta Dynamics Pvt. Ltd. All rights reserved.</div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            All systems operational
          </div>
        </div>
      </div>
    </footer>
  );
}
